// Route prefetch registry.
// Each entry maps a path matcher to a chunk importer. Vite/Rollup deduplicates
// repeated dynamic imports so calling these is safe and effectively a no-op
// after the chunk is cached.

type Importer = () => Promise<unknown>;

export interface RouteEntry {
  match: (path: string) => boolean;
  load: Importer;
  // Priority: lower = prefetched earlier during idle warmup.
  priority: number;
}

// Importers MUST be referentially stable to share the chunk cache with React.lazy.
export const importers = {
  latest: () => import("@/pages/LatestArticles.tsx"),
  article: () => import("@/pages/ArticlePage.tsx"),
  category: () => import("@/pages/CategoryPage.tsx"),
  search: () => import("@/pages/SearchPage.tsx"),
  contact: () => import("@/pages/ContactPage.tsx"),
  terms: () => import("@/pages/TermsOfService.tsx"),
  privacy: () => import("@/pages/PrivacyPolicy.tsx"),
  admin: () => import("@/pages/AdminPanel.tsx"),
  notFound: () => import("@/pages/NotFound.tsx"),
};

export const routes: RouteEntry[] = [
  { match: (p) => p === "/latest", load: importers.latest, priority: 1 },
  { match: (p) => p.startsWith("/category/"), load: importers.category, priority: 1 },
  { match: (p) => p.startsWith("/article/"), load: importers.article, priority: 1 },
  { match: (p) => p.startsWith("/search"), load: importers.search, priority: 2 },
  { match: (p) => p === "/contact", load: importers.contact, priority: 3 },
  { match: (p) => p === "/terms", load: importers.terms, priority: 4 },
  { match: (p) => p === "/privacy", load: importers.privacy, priority: 4 },
  { match: (p) => p === "/periodical-today-admin", load: importers.admin, priority: 5 },
];

const inFlight = new Set<Importer>();

export function prefetchRoute(path: string): void {
  const entry = routes.find((r) => r.match(path));
  if (!entry) return;
  prefetchImporter(entry.load);
}

export function prefetchImporter(load: Importer): void {
  if (inFlight.has(load)) return;
  inFlight.add(load);
  // Fire and forget — errors are tolerated (offline, chunk evicted, etc).
  load().catch(() => inFlight.delete(load));
}

// Network/device guards for mobile + slow connections.
interface NavigatorWithConnection extends Navigator {
  connection?: {
    saveData?: boolean;
    effectiveType?: string;
  };
  deviceMemory?: number;
}

export function isLowEndConnection(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as NavigatorWithConnection;
  const conn = nav.connection;
  if (conn?.saveData) return true;
  if (conn?.effectiveType && /(^|-)(2g|slow-2g)$/i.test(conn.effectiveType)) return true;
  if (typeof nav.deviceMemory === "number" && nav.deviceMemory <= 1) return true;
  return false;
}

type IdleCallback = (deadline: { timeRemaining: () => number; didTimeout: boolean }) => void;
type WindowWithIdle = Window & {
  requestIdleCallback?: (cb: IdleCallback, opts?: { timeout?: number }) => number;
  cancelIdleCallback?: (handle: number) => void;
};

export function onIdle(cb: () => void, timeout = 2000): () => void {
  if (typeof window === "undefined") return () => {};
  const w = window as WindowWithIdle;
  if (w.requestIdleCallback) {
    const id = w.requestIdleCallback(() => cb(), { timeout });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(cb, 200);
  return () => window.clearTimeout(id);
}

// Warm every route chunk in priority order during idle time. Skips on low-end
// networks where the cost outweighs the benefit.
export function warmAllRoutes(): () => void {
  if (isLowEndConnection()) return () => {};
  const queue = [...routes].sort((a, b) => a.priority - b.priority);
  let cancelled = false;

  const pump = () => {
    if (cancelled) return;
    const next = queue.shift();
    if (!next) return;
    prefetchImporter(next.load);
    onIdle(pump, 3000);
  };

  const cancelFirst = onIdle(pump, 3000);
  return () => {
    cancelled = true;
    cancelFirst();
  };
}
